import { t } from "i18next";
import { SetStateAction } from "react";
import { DateTime } from "luxon";
import { FleetManagementRow } from "../types/FleetManagement-row.types";
import {
  FleetContractDetails,
  FleetManagementPermissions,
  FleetFuelType,
  FleetStatus
} from "../types/FleetManagement.types";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";

const fleetStatusOrder: Record<FleetStatus, number> = {
  [FleetStatus.Available]: 0,
  [FleetStatus.Booked]: 1,
  [FleetStatus.Blocked]: 2
};

/**
 * Compare two fleet rows for table sorting: by status first (Available, Booked, Blocked),
 * then within same status by location (rows with location first).
 * Returns negative if a before b, positive if a after b, 0 if equal.
 */
export function compareFleetStatusThenLocation(
  a: FleetManagementRow,
  b: FleetManagementRow
): number {
  const statusDiff = fleetStatusOrder[a.status] - fleetStatusOrder[b.status];

  if (statusDiff !== 0) return statusDiff;

  const hasLocA = a.location?.id == null ? 0 : 1;
  const hasLocB = b.location?.id == null ? 0 : 1;

  return hasLocB - hasLocA;
}

/**
 * Computes vehicle status as of a given date from blocking events and bookings.
 * Blocking takes precedence over booking.
 */
export function getStatusOnDate(
  vehicle: FleetManagementRow,
  date: DateTime
): { status: FleetStatus; blockedReason?: string | null } {
  const dayStart = date.startOf("day");
  const dayEnd = date.endOf("day");

  const isBlocked =
    vehicle.blockedEvents?.some(block => {
      const blockStart = DateTime.fromISO(block.start);
      const blockEnd = DateTime.fromISO(block.end);
      return (
        (blockStart <= dayStart && blockEnd >= dayStart) ||
        (blockStart <= dayEnd && blockEnd >= dayEnd) ||
        (blockStart >= dayStart && blockEnd <= dayEnd)
      );
    }) ?? false;

  if (isBlocked) {
    const block = vehicle.blockedEvents?.find(b => {
      const blockStart = DateTime.fromISO(b.start);
      const blockEnd = DateTime.fromISO(b.end);
      return (
        (blockStart <= dayStart && blockEnd >= dayStart) ||
        (blockStart <= dayEnd && blockEnd >= dayEnd) ||
        (blockStart >= dayStart && blockEnd <= dayEnd)
      );
    });
    return {
      status: FleetStatus.Blocked,
      blockedReason: block?.reason ?? vehicle.blockedReason ?? null
    };
  }

  const isBooked =
    vehicle.occupiedBooking?.some(booking => {
      const bookingStart = DateTime.fromISO(booking.startTime);
      const bookingEnd = DateTime.fromISO(booking.endTime);
      return (
        (bookingStart <= dayStart && bookingEnd >= dayStart) ||
        (bookingStart <= dayEnd && bookingEnd >= dayEnd) ||
        (bookingStart >= dayStart && bookingEnd <= dayEnd)
      );
    }) ?? false;

  if (isBooked) {
    return { status: FleetStatus.Booked };
  }

  return { status: FleetStatus.Available };
}

/**
 * Computes vehicle status for a date range (for filtered view).
 * When a vehicle is shown because it has an event in the range, status reflects that:
 * Blocked if any blocking overlaps range, else Booked if any booking overlaps range, else Available.
 */
export function getStatusForDateRange(
  vehicle: FleetManagementRow,
  rangeStart: DateTime,
  rangeEnd: DateTime
): { status: FleetStatus; blockedReason?: string | null } {
  const hasBlockingInRange =
    vehicle.blockedEvents?.some(block => {
      const blockStart = DateTime.fromISO(block.start);
      const blockEnd = DateTime.fromISO(block.end);
      return (
        (blockStart <= rangeStart && blockEnd >= rangeStart) ||
        (blockStart <= rangeEnd && blockEnd >= rangeEnd) ||
        (blockStart >= rangeStart && blockEnd <= rangeEnd)
      );
    }) ?? false;

  if (hasBlockingInRange) {
    const block = vehicle.blockedEvents?.find(b => {
      const blockStart = DateTime.fromISO(b.start);
      const blockEnd = DateTime.fromISO(b.end);
      return (
        (blockStart <= rangeStart && blockEnd >= rangeStart) ||
        (blockStart <= rangeEnd && blockEnd >= rangeEnd) ||
        (blockStart >= rangeStart && blockEnd <= rangeEnd)
      );
    });
    return {
      status: FleetStatus.Blocked,
      blockedReason: block?.reason ?? vehicle.blockedReason ?? null
    };
  }

  const hasBookingInRange =
    vehicle.occupiedBooking?.some(booking => {
      const bookingStart = DateTime.fromISO(booking.startTime);
      const bookingEnd = DateTime.fromISO(booking.endTime);
      return (
        (bookingStart <= rangeStart && bookingEnd >= rangeStart) ||
        (bookingStart <= rangeEnd && bookingEnd >= rangeEnd) ||
        (bookingStart >= rangeStart && bookingEnd <= rangeEnd)
      );
    }) ?? false;

  if (hasBookingInRange) {
    return { status: FleetStatus.Booked };
  }

  return { status: FleetStatus.Available };
}

/** Returns true if the given range [start, end] overlaps [dateRange.start, dateRange.end]. */
function rangeOverlaps(
  start: DateTime,
  end: DateTime,
  dateRange: { start: DateTime; end: DateTime }
): boolean {
  return (
    (start >= dateRange.start && start <= dateRange.end) ||
    (end >= dateRange.start && end <= dateRange.end) ||
    (start <= dateRange.start && end >= dateRange.end)
  );
}

/**
 * When a date range is set (editable view): filter to vehicles that have a booking or block
 * in the range, and enrich each row with status and blockedReason for that range.
 */
export function filterFleetsByDateRangeWithStatus(
  fleets: FleetManagementRow[],
  dateRange: { start: DateTime; end: DateTime }
): FleetManagementRow[] {
  return fleets
    .filter(item => {
      const hasBookingInRange =
        item.occupiedBooking?.some(booking =>
          rangeOverlaps(
            DateTime.fromISO(booking.startTime),
            DateTime.fromISO(booking.endTime),
            dateRange
          )
        ) ?? false;

      const hasBlockedInRange =
        item.blockedEvents?.some(block =>
          rangeOverlaps(DateTime.fromISO(block.start), DateTime.fromISO(block.end), dateRange)
        ) ?? false;

      return hasBookingInRange || hasBlockedInRange;
    })
    .map(item => {
      const { status, blockedReason } = getStatusForDateRange(item, dateRange.start, dateRange.end);
      return { ...item, status, blockedReason };
    });
}

export type CheckFleetValType =
  | "name"
  | "description"
  | "licensePlate"
  | "brandModel"
  | "numSeats"
  | "odometer"
  | "adminNotes"
  | "reach";

export const checkFleetItemVal = (
  value: string,
  item: CheckFleetValType,
  validationErrors: Record<string, string | undefined>,
  setValidationErrors: (value: SetStateAction<Record<string, string | undefined>>) => void
) => {
  if (value === "") {
    setValidationErrors({
      ...validationErrors,
      [item]: undefined
    });
  } else if (item === "reach") {
    const n = Number(value);
    if (Number.isNaN(n) || n > 0) {
      setValidationErrors({
        ...validationErrors,
        reach: undefined
      });
    } else {
      setValidationErrors({
        ...validationErrors,
        reach: t("Reach must be greater than 0")
      });
    }
  } else {
    checkFleetItemValueTypeLength(value, item, validationErrors, setValidationErrors);
  }
};

export const checkFleetItemValueTypeLength = (
  value: string,
  item: CheckFleetValType,
  validationErrors: Record<string, string | undefined>,
  setValidationErrors: (value: SetStateAction<Record<string, string | undefined>>) => void
) => {
  const resetValidErr = () =>
    setValidationErrors({
      ...validationErrors,
      [item]: undefined
    });

  const doNotExceedChars = (item: CheckFleetValType, limit: number) => {
    if (value.length > limit) {
      setValidationErrors({
        ...validationErrors,
        [item]: t(`${item} cannot exceed ${limit} characters`)
      });
    } else resetValidErr();
  };

  switch (item) {
    case "name": {
      doNotExceedChars("name", 75);
      break;
    }
    case "description": {
      doNotExceedChars("description", 1000);
      break;
    }
    case "licensePlate": {
      doNotExceedChars("licensePlate", 10);
      break;
    }
    case "brandModel": {
      doNotExceedChars("brandModel", 30);
      break;
    }
    case "numSeats": {
      doNotExceedChars("numSeats", 50);
      break;
    }
  }
};

function appendOptionalStrings(
  data: FormData,
  fields: Array<{ key: string; value: string | number | null | undefined }>
): void {
  for (const { key, value } of fields) {
    const str = typeof value === "number" ? String(value) : value ?? "";
    data.append(key, str);
  }
}

export function appendContractToFormData(data: FormData, contract: FleetContractDetails): void {
  data.append("contractVendor", contract.vendor ?? "");
  data.append("contractId", contract.contractId ?? "");
  data.append("contractStart", contract.start ?? "");
  data.append("contractEnd", contract.end ?? "");

  const rawLeaseRate = contract.leaseRate;
  let leaseRate: number | null = null;
  if (typeof rawLeaseRate === "number" && !Number.isNaN(rawLeaseRate)) {
    leaseRate = rawLeaseRate;
  } else if (typeof rawLeaseRate === "string") {
    const parsed = Number(rawLeaseRate);
    if (!Number.isNaN(parsed)) leaseRate = parsed;
  }
  data.append("leaseRate", leaseRate == null ? "" : String(leaseRate));
}

/**
 * Backend VehicleDTO.permissions shape for PATCH (companyIds, costCenterUnitIds, userIds).
 * Permissions are applied in the same PATCH as other vehicle fields.
 * Accepts either FleetManagementPermissions (companies/costCenterUnits/users) or id arrays (companyIds/costCenterUnitIds/userIds).
 */
function fleetPermissionsToVehicleDtoPermissions(
  fleetPermissions:
    | FleetManagementPermissions
    | {
        companyIds?: string[];
        costCenterUnitIds?: string[];
        userIds?: string[];
      }
): {
  companyIds: string[];
  costCenterUnitIds: string[];
  userIds: string[];
} {
  const toIdsFromObjects = (arr: { id: string }[] | undefined) =>
    (arr ?? []).map(o => String(o.id));
  const toIdsFromStrings = (arr: string[] | undefined) => [...(arr ?? [])];

  const perms = fleetPermissions as Record<string, unknown>;
  const hasIdArrays =
    Array.isArray(perms.companyIds) ||
    Array.isArray(perms.costCenterUnitIds) ||
    Array.isArray(perms.userIds);

  if (hasIdArrays) {
    return {
      companyIds: toIdsFromStrings(perms.companyIds as string[] | undefined),
      costCenterUnitIds: toIdsFromStrings(perms.costCenterUnitIds as string[] | undefined),
      userIds: toIdsFromStrings(perms.userIds as string[] | undefined)
    };
  }
  return {
    companyIds: toIdsFromObjects(perms.companies as { id: string }[] | undefined),
    costCenterUnitIds: toIdsFromObjects(perms.costCenterUnits as { id: string }[] | undefined),
    userIds: toIdsFromObjects(perms.users as { id: string }[] | undefined)
  };
}

/**
 * Builds FormData for POST /fleet/vehicles (create) and PATCH /fleet/vehicles/:id (update).
 * Aligned with backend spec: flat contract fields; permissions sent as { companyIds, costCenterUnitIds, userIds }
 * in the same body and applied by the backend in the same PATCH. Vehicle attachments use POST /fleet/vehicles/:id/attachments.
 */
export function fleetRowValuesToFormData(
  value: Partial<FleetManagementRow>,
  rowOriginal: FleetManagementRow,
  userId: string,
  imageFiles: FileObject[]
): FormData {
  const data = new FormData();

  // Removed (not in backend create/update body):
  data.append("userId", userId);
  data.append("id", String(value.id ? Number(value.id) : 1));

  if (value.fleetPermissions) {
    data.append(
      "permissions",
      JSON.stringify(fleetPermissionsToVehicleDtoPermissions(value.fleetPermissions))
    );
  }

  // Resolve mandatory vehicle fields once: value → rowOriginal → empty/default
  const name = value.name ?? rowOriginal.name ?? "";
  const description = value.description ?? rowOriginal.description ?? "";
  const licensePlate = value.licensePlate ?? rowOriginal.licensePlate ?? "";
  const brandModel = value.brandModel ?? rowOriginal.brandModel ?? "";
  const vehicleType = value.vehicleType ?? rowOriginal.vehicleType ?? "";
  const fuel = value.fuel ?? rowOriginal.fuel ?? FleetFuelType.Gasoline;
  const reach = value.reach ?? rowOriginal.reach ?? 0;
  const numSeats = value.numSeats ?? rowOriginal.numSeats ?? 0;
  const status = value.status ?? rowOriginal.status ?? FleetStatus.Available;
  const odometer = value.odometer ?? rowOriginal.odometer ?? 0;
  const companyId = value.companyId ?? rowOriginal.companyId ?? 0;

  data.append("name", String(name));
  data.append("description", String(description));
  data.append("licensePlate", String(licensePlate));
  data.append("brandModel", String(brandModel));
  data.append("vehicleType", String(vehicleType));
  data.append("fuel", String(fuel));
  data.append("reach", String(reach));
  data.append("numSeats", String(numSeats));
  data.append("status", String(status));
  data.append("odometer", String(odometer));
  data.append("companyId", String(companyId));

  appendOptionalStrings(data, [
    { key: "blockedReason", value: value.blockedReason },
    { key: "adminNotes", value: value.adminNotes },
    { key: "userNotes", value: value.userNotes },
    { key: "bufferMinutes", value: value.bufferMinutes },
    { key: "pricePerHour", value: value.pricePerHour }
  ]);

  if (value.contractDetails) {
    appendContractToFormData(data, value.contractDetails);
  }

  data.append("locationInventoryId", String(value.location?.id ?? 0));

  if (value.properties && value.properties.length > 0) {
    data.append("properties", JSON.stringify(value.properties));
  }

  if (value.picturePaths && value.picturePaths.length > 0) {
    data.append("picturePaths", JSON.stringify(value.picturePaths));
  }

  if (imageFiles.length) imageFiles.forEach(({ file }) => data.append("files", file, file.name));

  return data;
}
